/**
 * utils/stepSequenceBuilder.ts
 *
 * Builds the ordered step sequence for the client form experience.
 *
 * New intra-form condition system (Feb 2026):
 * - Conditions reference other fields in the same form (single/multiple choice)
 * - Topological sort ensures a conditioning field always appears before the
 *   fields it controls, regardless of the creator's original ordering.
 * - Fields are filtered by current answers: a conditioned field is only
 *   shown if its source field's answer matches one of condition_values.
 * - Cascading visibility: if a source field is itself hidden, all fields
 *   depending on it are also hidden.
 *
 * Updated Mar 2026: Replaced single condition with conditions[] array (OR logic).
 * A field is visible when AT LEAST ONE condition rule is satisfied.
 *
 * Fixed Mar 2026: isFieldVisible now creates a branch-local copy of visited for
 * each OR branch so sibling branches cannot interfere with each other's traversal.
 *
 * Updated Apr 2026: condition_operator support. When operator is 'and', ALL condition
 * rules must be satisfied. When operator is 'or' (default), at least one must pass.
 * Updated Apr 2026: findInitialStep now surfaces 'info' steps as the next step
 * (returns i) instead of skipping them, so users see info callouts on fresh start.
 * Fixed Apr 2026: multiple_choice answers are stored as CSV ("A,B,C"), not JSON.
 * evalCondition now splits on comma when source field type is multiple_choice so
 * conditions depending on multi-select fields evaluate correctly.
 */

import type { RequestField } from './previewHelpers';

export type StepItem = { kind: 'field'; field: RequestField };

/**
 * Check if a field is visible given the current set of answers.
 * Uses field.condition_operator to determine how rules are combined:
 *   'or'  → visible if at least one condition rule is satisfied (default)
 *   'and' → visible only if ALL condition rules are satisfied simultaneously
 * Recursively checks that source fields are also visible (cascading).
 * The visited set guards against infinite recursion on cyclic condition graphs.
 */
export function isFieldVisible(
  fieldId: string,
  fieldsMap: Map<string, RequestField>,
  answers: Record<string, string>,
  visited: Set<string> = new Set()
): boolean {
  if (visited.has(fieldId)) return false;
  const field = fieldsMap.get(fieldId);
  if (!field) return false;
  if (!field.conditions || field.conditions.length === 0) return true;

  const useAndLogic = field.condition_operator === 'and';

  const evalCondition = (condition: (typeof field.conditions)[number]): boolean => {
    const branchVisited = new Set(visited);
    branchVisited.add(fieldId);
    if (!isFieldVisible(condition.source_field_id, fieldsMap, answers, branchVisited)) return false;

    const answer = answers[condition.source_field_id];
    if (!answer) return false;

    const conditionValues = condition.values;
    if (conditionValues.length === 0) return true;

    const sourceField = fieldsMap.get(condition.source_field_id);
    if (sourceField?.type === 'multiple_choice') {
      const selectedValues = answer.split(',').map((v) => v.trim()).filter(Boolean);
      return selectedValues.some((v) => conditionValues.includes(v));
    }

    try {
      const parsed = JSON.parse(answer);
      if (Array.isArray(parsed)) {
        return parsed.some((v) => conditionValues.includes(v));
      }
    } catch {}

    return conditionValues.includes(answer);
  };

  return useAndLogic
    ? field.conditions.every(evalCondition)
    : field.conditions.some(evalCondition);
}

/**
 * Topological sort of fields ensuring each source field comes before
 * the fields it conditions. Original position is the tiebreaker.
 * Updated Mar 2026: visits all condition sources (multi-source support).
 */
function topologicalSort(fields: RequestField[]): RequestField[] {
  const fieldMap = new Map(fields.map((f) => [f.id, f]));
  const visited = new Set<string>();
  const result: RequestField[] = [];

  function visit(field: RequestField) {
    if (visited.has(field.id)) return;
    visited.add(field.id);
    for (const condition of field.conditions || []) {
      if (fieldMap.has(condition.source_field_id)) {
        visit(fieldMap.get(condition.source_field_id)!);
      }
    }
    result.push(field);
  }

  const sorted = [...fields].sort((a, b) => a.position - b.position);
  for (const field of sorted) {
    visit(field);
  }

  return result;
}

/**
 * Builds the ordered, filtered step sequence for the client form.
 * Only visible fields (based on current answers) are included.
 */
export function buildStepSequence(
  allFields: RequestField[],
  currentAnswers: Record<string, string>
): StepItem[] {
  const sorted = topologicalSort(allFields);
  const fieldsMap = new Map(allFields.map((f) => [f.id, f]));

  return sorted
    .filter((field) => isFieldVisible(field.id, fieldsMap, currentAnswers))
    .map((field) => ({ kind: 'field' as const, field }));
}

/**
 * Finds the first unanswered step index for session resumption.
 * Info fields are always surfaced as the next step when encountered,
 * since there is no saved-response signal to indicate they were seen.
 */
export function findInitialStep(
  sequence: StepItem[],
  savedResponses: Record<string, string>,
  savedDocumentFieldIds: Set<string>
): number {
  for (let i = 0; i < sequence.length; i++) {
    const { field } = sequence[i];
    if (field.type === 'info') return i;
    if (field.type === 'document') {
      if (!savedDocumentFieldIds.has(field.id)) return i;
    } else {
      if (!savedResponses[field.id]) return i;
    }
  }
  return Math.max(0, sequence.length - 1);
}
